import React, { useEffect, useState, useCallback } from "react";
import api from "../../api";
import { Loader2, CreditCard, CheckCircle2, AlertCircle, Zap, ChevronDown } from "lucide-react";
import { toast } from "../../hooks/use-toast";

export default function StripeTab() {
  const [connect, setConnect] = useState(null); // {connected, account_id, details_submitted}
  const [sub, setSub] = useState(null); // {status, monthly_fee}
  const [payout, setPayout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [showLegacy, setShowLegacy] = useState(false);
  const [keys, setKeys] = useState({ stripe_pk: "", stripe_sk: "" });

  const refresh = useCallback(async () => {
    try {
      const [c, s] = await Promise.all([
        api.get("/business/connect/status"),
        api.get("/business/subscription/status"),
      ]);
      setConnect(c.data);
      setSub(s.data);
      api.get("/business/payout").then(({ data }) => setPayout(data)).catch(() => {});
    } catch (e) { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    refresh();
    // Handle Stripe redirect returns
    const params = new URLSearchParams(window.location.search);
    if (params.get("sub") === "success" && params.get("session_id")) {
      api.get(`/business/subscription/confirm?session_id=${params.get("session_id")}`)
        .then(() => { toast({ title: "Subscription active", description: "Your plan is now active." }); refresh(); })
        .catch(() => {});
    }
    if (params.get("connect") === "return") {
      toast({ title: "Welcome back", description: "Checking your Stripe connection…" });
      setTimeout(refresh, 800);
    }
  }, [refresh]);

  const onboard = async () => {
    setBusy("connect");
    try {
      const { data } = await api.post("/business/connect/onboard", { origin: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      toast({ title: "Stripe Connect unavailable", description: err?.response?.data?.detail || "Connect is not enabled yet.", variant: "destructive" });
      setBusy("");
    }
  };

  const subscribe = async () => {
    setBusy("sub");
    try {
      const { data } = await api.post("/business/subscription/checkout", { origin: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      toast({ title: "Could not start subscription", description: err?.response?.data?.detail || "Try again.", variant: "destructive" });
      setBusy("");
    }
  };

  const requestPayout = async () => {
    setBusy("payout");
    try {
      await api.post("/business/payout/request", {});
      toast({ title: "Payout requested", description: "We'll process your payout and mark it paid once sent." });
      await refresh();
    } catch (err) {
      toast({ title: "Couldn't request payout", description: err?.response?.data?.detail || "Try again.", variant: "destructive" });
    } finally { setBusy(""); }
  };

  const saveLegacy = async (e) => {
    e.preventDefault();
    setBusy("legacy");
    try {
      await api.put("/business/stripe", keys);
      toast({ title: "Stripe keys saved" });
    } catch (err) {
      toast({ title: "Error", description: "Failed to save keys", variant: "destructive" });
    } finally { setBusy(""); }
  };

  if (loading) return <Loader2 className="animate-spin text-[#2ec9c9]" size={32} />;

  const connected = connect?.connected;
  const subActive = sub?.status === "active";

  return (
    <div className="max-w-[620px] space-y-6">
      {/* Connect account */}
      <div className="bg-white rounded-2xl p-6 shadow-[0_8px_24px_rgba(20,30,55,0.06)]" data-testid="connect-card">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-11 h-11 rounded-xl bg-[#eefafa] flex items-center justify-center"><CreditCard className="text-[#2ec9c9]" size={22} /></div>
          <div>
            <h3 className="font-semibold text-[#16243b]">Connect your Stripe account</h3>
            <p className="text-sm text-[#8a97a8]">Get paid directly. We automatically deduct a 5% platform fee per sale.</p>
          </div>
        </div>

        {connected ? (
          <div className="bg-green-50 text-green-700 rounded-xl p-3 flex items-center gap-2 text-sm mb-4" data-testid="connect-status-connected">
            <CheckCircle2 size={18} /> Stripe connected — payouts enabled. Sales pay out to you automatically, minus the 5% platform fee.
          </div>
        ) : connect?.account_id ? (
          <div className="bg-amber-50 text-amber-700 rounded-xl p-3 flex items-center gap-2 text-sm mb-4" data-testid="connect-status-pending">
            <AlertCircle size={18} /> Onboarding started but not finished. Click below to complete your Stripe setup.
          </div>
        ) : (
          <div className="bg-slate-50 text-[#5a6b80] rounded-xl p-3 flex items-center gap-2 text-sm mb-4" data-testid="connect-status-none">
            <AlertCircle size={18} /> Not connected yet. Connect Stripe to start receiving payouts.
          </div>
        )}

        <button onClick={onboard} disabled={busy === "connect"} data-testid="connect-stripe-btn"
          className="gp-btn-teal rounded-xl px-6 py-3 font-medium disabled:opacity-60 inline-flex items-center gap-2">
          {busy === "connect" ? <Loader2 className="animate-spin" size={18} /> : <Zap size={18} />}
          {connected ? "Manage Stripe account" : connect?.account_id ? "Finish Stripe setup" : "Connect Stripe"}
        </button>
      </div>

      {/* Platform subscription */}
      <div className="bg-white rounded-2xl p-6 shadow-[0_8px_24px_rgba(20,30,55,0.06)]" data-testid="subscription-card">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-semibold text-[#16243b]">Platform subscription · {sub?.plan_name || "Plan"}</h3>
            <p className="text-sm text-[#8a97a8]">{sub && sub.monthly_fee <= 0 ? "Free plan — no monthly fee." : `£${(sub?.monthly_fee ?? 0).toFixed(2)}/month — billed automatically.`} <a href="/dashboard?tab=plans" className="text-[#1fb0b0] hover:underline" data-testid="stripe-tab-plans-link">Compare plans</a></p>
          </div>
          <span data-testid="subscription-status" className={`px-3 py-1 rounded-full text-xs font-semibold ${subActive ? "bg-green-100 text-green-700" : "bg-slate-100 text-[#5a6b80]"}`}>
            {subActive ? "Active" : (sub?.status || "inactive")}
          </span>
        </div>
        {!subActive && sub && sub.monthly_fee > 0 && (
          <button onClick={subscribe} disabled={busy === "sub"} data-testid="subscribe-btn"
            className="gp-btn-outline rounded-xl px-6 py-3 font-medium disabled:opacity-60 inline-flex items-center gap-2">
            {busy === "sub" ? <Loader2 className="animate-spin" size={18} /> : null}
            Subscribe £{(sub?.monthly_fee ?? 0).toFixed(2)}/mo
          </button>
        )}
      </div>

      {/* Payout visibility */}
      {payout && (
        <div className="bg-white rounded-2xl p-6 shadow-[0_8px_24px_rgba(20,30,55,0.06)]" data-testid="payout-card">
          <h3 className="font-semibold text-[#16243b] mb-1">Your payouts</h3>
          <p className="text-sm text-[#8a97a8] mb-4">Based on {payout.sales_count} paid voucher(s). Platform fee is {payout.commission_pct}%{payout.commission_fixed ? ` + £${payout.commission_fixed.toFixed(2)}` : ""} per sale.</p>
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-[#f6f8fb] rounded-xl p-4"><p className="text-xs text-[#8a97a8]">Gross sales</p><p className="text-lg font-bold text-[#16243b]">£{payout.gross_sales.toFixed(2)}</p></div>
            <div className="bg-[#f6f8fb] rounded-xl p-4"><p className="text-xs text-[#8a97a8]">Platform fee</p><p className="text-lg font-bold text-[#f47c7c]">−£{payout.platform_fee.toFixed(2)}</p></div>
            <div className="bg-[#eefafa] rounded-xl p-4"><p className="text-xs text-[#8a97a8]">Upcoming payout</p><p className="text-lg font-bold text-[#16243b]" data-testid="net-payout">£{payout.net_payout.toFixed(2)}</p></div>
          </div>
          {!payout.stripe_connected && <p className="text-xs text-[#8a97a8] mt-3">Connect Stripe above to receive these payouts automatically.</p>}
        </div>
      )}

      {/* Platform-held funds + request payout (for sales collected via the platform) */}
      {payout && payout.held_net > 0 && (
        <div className="bg-white rounded-2xl p-6 shadow-[0_8px_24px_rgba(20,30,55,0.06)]" data-testid="held-funds-card">
          <h3 className="font-semibold text-[#16243b] mb-1">Funds we're holding for you</h3>
          <p className="text-sm text-[#8a97a8] mb-4">Sales collected on your behalf (before you connected Stripe). Request a payout whenever it suits you and we'll transfer it manually.</p>
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="bg-[#f6f8fb] rounded-xl p-4"><p className="text-xs text-[#8a97a8]">Collected (net of fees)</p><p className="text-lg font-bold text-[#16243b]">£{payout.held_net.toFixed(2)}</p></div>
            <div className="bg-[#f6f8fb] rounded-xl p-4"><p className="text-xs text-[#8a97a8]">Already paid out</p><p className="text-lg font-bold text-[#5a6b80]">£{payout.paid_out.toFixed(2)}</p></div>
            <div className="bg-[#eefafa] rounded-xl p-4"><p className="text-xs text-[#8a97a8]">Available now</p><p className="text-lg font-bold text-[#16243b]" data-testid="available-balance">£{payout.available_balance.toFixed(2)}</p></div>
          </div>
          {payout.pending_request ? (
            <div className="bg-amber-50 text-amber-700 rounded-xl p-3 flex items-center gap-2 text-sm" data-testid="payout-pending">
              <AlertCircle size={18} /> Payout of £{payout.pending_request.amount.toFixed(2)} requested — awaiting processing.
            </div>
          ) : (
            <button onClick={requestPayout} disabled={busy === "payout" || payout.available_balance <= 0}
              data-testid="request-payout-btn" className="gp-btn-teal rounded-xl px-6 py-3 font-medium disabled:opacity-60 inline-flex items-center gap-2">
              {busy === "payout" ? <Loader2 className="animate-spin" size={18} /> : null}
              Request payout of £{payout.available_balance.toFixed(2)}
            </button>
          )}
          {payout.requests && payout.requests.length > 0 && (
            <div className="mt-4 border-t border-gray-50 pt-3">
              <p className="text-xs font-medium text-[#8a97a8] mb-2">Payout history</p>
              <div className="space-y-1">
                {payout.requests.map((r) => (
                  <div key={r.id} className="flex items-center justify-between text-sm" data-testid={`payout-request-${r.id}`}>
                    <span className="text-[#5a6b80]">{String(r.created_at).slice(0, 10)} — £{r.amount.toFixed(2)}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${r.status === "paid" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>{r.status === "paid" ? "Paid" : "Requested"}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Legacy keys (advanced fallback) */}
      <div className="bg-white rounded-2xl p-4 shadow-[0_8px_24px_rgba(20,30,55,0.06)]">
        <button onClick={() => setShowLegacy((v) => !v)} className="w-full flex items-center justify-between text-sm text-[#8a97a8]" data-testid="legacy-toggle">
          <span>Advanced: use your own Stripe API keys instead</span>
          <ChevronDown size={16} className={`transition-transform ${showLegacy ? "rotate-180" : ""}`} />
        </button>
        {showLegacy && (
          <form onSubmit={saveLegacy} className="space-y-3 mt-4">
            <p className="text-xs text-[#8a97a8]">Only for advanced setups. With Connect above, you don't need this.</p>
            <input value={keys.stripe_pk} onChange={(e) => setKeys({ ...keys, stripe_pk: e.target.value })} placeholder="pk_test_…"
              className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] font-mono text-sm" />
            <input type="password" value={keys.stripe_sk} onChange={(e) => setKeys({ ...keys, stripe_sk: e.target.value })} placeholder="sk_test_…"
              className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] font-mono text-sm" />
            <button disabled={busy === "legacy"} className="gp-btn-outline rounded-xl px-5 py-2.5 text-sm font-medium disabled:opacity-60">Save keys</button>
          </form>
        )}
      </div>
    </div>
  );
}
